/**
 * src/shell/employee_pillars.js
 *
 * Employee self-service IA (EMPLOYEE_PORTAL_PLAN §2, 2026-07-14). The
 * employee/supervisor persona does NOT get the admin cockpit's 5 labeled
 * pillars — it gets a FLAT 6-item nav that mirrors the mobile bottom-tab
 * mental model (Home · Time · Leave · Tasks & Requests · Pay · More).
 *
 * This file is the single source of truth for that flat nav (mirrors the
 * shape of pillars.js's PILLARS/ALL_SCREENS so employee_shell.js can reuse
 * shell.js's proven router-registration + active-nav pattern, just smaller).
 * It carries NO per-item role gate: every destination is inherently
 * self-scoped ("my …"), so an employee and a supervisor see the identical
 * nav — the only behavioural difference (a supervisor's approvals surfacing
 * under Tasks & Requests) is decided server-side inside getMyWork, never by
 * hiding a nav entry (plan §5.2).
 *
 * Screen mounters for every path except '/me' (Home) are wired in
 * employee_shell.js as placeholders until the Wave-3 screens (my_attendance /
 * my_leave / my_tasks / my_payslip / more) land — the paths here are the
 * stable route contract those screens plug into.
 */

import { ICONS } from './pillars.js';

// pillars.js has no house / more glyph — the two employee-only nav icons live
// here, same 15x15 stroke language as ICONS.
const ICON_HOME = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/><path d="M9.5 21v-6h5v6"/></svg>';
const ICON_MORE = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>';

/** The employee shell's default landing route (Home). */
export const EMPLOYEE_HOME_PATH = '/me';

/**
 * The flat 6-item employee nav. `labelKey` resolves through t() at render
 * time (so it flips with the language toggle); `path` is the hash route each
 * item navigates straight to (no `/pillar/{id}` springboard indirection).
 */
export const EMPLOYEE_NAV = [
  { id: 'home',     path: EMPLOYEE_HOME_PATH, labelKey: 'employee.navHome',     icon: ICON_HOME },
  { id: 'time',     path: '/my-attendance',   labelKey: 'employee.navTime',     icon: ICONS.attendance },
  { id: 'leave',    path: '/my-leave',        labelKey: 'employee.navLeave',    icon: ICONS.leave },
  { id: 'requests', path: '/my-tasks',        labelKey: 'employee.navRequests', icon: ICONS.tasks },
  { id: 'pay',      path: '/my-payslip',      labelKey: 'employee.navPay',      icon: ICONS.payroll },
  { id: 'more',     path: '/my-more',         labelKey: 'employee.navMore',     icon: ICON_MORE },
];

/** Every employee route path (used to detect a stale/foreign hash and
 * redirect it onto Home). */
export const EMPLOYEE_PATHS = new Set(EMPLOYEE_NAV.map((i) => i.path));

/** True if `path` is one of the employee shell's own nav routes. */
export function isEmployeePath(path) {
  return EMPLOYEE_PATHS.has(path);
}

/** Resolve a route path back to its nav item (for active-nav highlight +
 * page title), or null for a non-nav / unknown path. */
export function employeeItemForPath(path) {
  return EMPLOYEE_NAV.find((i) => i.path === path) || null;
}
